import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react'
import { Button } from './Button'
import { DESIGN_COPY } from './copy'
import { toneClass, type ChipTone } from './tokens'

export const Dialog = ({
  open,
  title,
  onClose,
  children,
  footer,
  className,
  tone,
  edge = 'plain',
}: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  className?: string
  tone?: ChipTone
  edge?: 'plain' | 'hatch'
}) => {
  const headingId = `${title}-heading`
  const dialogRef = useRef<HTMLDialogElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) return
    if (open && !dialog.open) {
      previouslyFocused.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
      dialog.showModal()
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [open])

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) return

    const handleCancel = (event: Event) => {
      event.preventDefault()
      onClose()
    }
    const handleClose = () => {
      previouslyFocused.current?.focus()
      previouslyFocused.current = null
    }

    dialog.addEventListener('cancel', handleCancel)
    dialog.addEventListener('close', handleClose)
    return () => {
      dialog.removeEventListener('cancel', handleCancel)
      dialog.removeEventListener('close', handleClose)
    }
  }, [onClose])

  const onBackdropClick = (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) onClose()
  }

  return (
    <dialog
      ref={dialogRef}
      className={['dialog', className].filter(Boolean).join(' ')}
      aria-labelledby={headingId}
      onClick={onBackdropClick}
    >
      {open ? (
        <div className="dialog-card">
          {edge === 'hatch' ? (
            <div className={`dialog-edge ${toneClass(tone ?? 'neutral')}`} aria-hidden="true" />
          ) : null}
          <div className="dialog-head">
            <h2 id={headingId}>{title}</h2>
            <Button variant="ghost" onClick={onClose} aria-label={DESIGN_COPY.dialogClose}>
              {DESIGN_COPY.dialogClose}
            </Button>
          </div>
          <div className="dialog-body">{children}</div>
          {footer === undefined ? null : <div className="dialog-foot">{footer}</div>}
        </div>
      ) : null}
    </dialog>
  )
}
